import React, { useEffect, useMemo, useState } from 'react'
import {
  SelectArrayInputProps,
  AutocompleteArrayInput,
  useLocaleState,
  useGetList,
  useTranslate,
  Identifier,
  RaRecord,
} from 'react-admin'
import { Stack, Button, Typography } from '@mui/material'
import { COMMON_FIELDS } from '../../providers/localization/constant'
import { Services } from '../../resources/services/type'
import { useFormContext } from 'react-hook-form'

const defaultProps = {
  source: 'services',
}

type ServiceSelect = {
  key: string
  name: string
  service_duration?: number // Add service_duration property to ServiceSelect type
}

interface ServiceMultiSelectInputProps extends SelectArrayInputProps {
  allServicesButton?: boolean
  slotDuration?: number
  servicesDuration?: number
  updateTotalDuration?: (duration: number) => void
  filterByBranchCode?: string
  setServices?: (services: string[]) => void
  value?: RaRecord<Identifier>[]
}

export const ServiceMultiSelectInput: React.FC<ServiceMultiSelectInputProps> = ({
  source,
  allServicesButton = true,
  slotDuration,
  servicesDuration,
  updateTotalDuration,
  filterByBranchCode,
  setServices,
  value = [],
  ...props
}) => {
  const translate = useTranslate()
  const [locale] = useLocaleState()
  const { setValue } = useFormContext()

  const { data, isLoading } = useGetList<Services>('services', {
    ...(filterByBranchCode && { filter: { branch_code: filterByBranchCode } }),
    sort: { field: 'create_at', order: 'ASC' },
    meta: { isPaginate: false },
  })
  const services: ServiceSelect[] = useMemo(() => {
    return data
      ? data.map((c) => ({
          key: c.key,
          name: `${c[`title_${locale}`] || c['title']} (${c.service_duration} นาที)`,
          service_duration: c.service_duration, // Map service_duration from the service object
        }))
      : []
  }, [data, locale])

  const [selectedServices, setSelectedServices] = useState(value as unknown as string[])

  if (updateTotalDuration !== undefined) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      const totalDuration = selectedServices.reduce((acc, serviceKey) => {
        const selectedService = services.find((service) => service.key === serviceKey)
        return acc + (selectedService?.service_duration || 0)
      }, 0)
      updateTotalDuration(totalDuration)
    }, [selectedServices, services, updateTotalDuration])
  }

  const handleSelectChange = (selectedItems): void => {
    setSelectedServices(selectedItems)
    if (setServices !== undefined) setServices(selectedItems)
  }

  return (
    <Stack>
      <AutocompleteArrayInput
        loading={isLoading}
        source={source}
        choices={services}
        optionValue='key'
        label={`${COMMON_FIELDS}.specific_services.label`}
        onChange={handleSelectChange} // Call the handleSelectChange function on selection change
        value={value}
        fullWidth
        {...props}
      />
      {allServicesButton && (
        <Button
          variant='contained'
          size='medium'
          onClick={(): void =>
            setValue(
              source,
              services.map((e) => e.key),
            )
          }
          sx={{ width: 'fit-content' }}
        >
          {translate('ra.action.select_all_services')}
        </Button>
      )}
      {slotDuration && slotDuration < servicesDuration && (
        <Typography variant='body1' color='error' fontWeight='bold'>
          เวลาบริการที่เลือกต้องน้อยกว่าเวลาจองทั้งหมด
        </Typography>
      )}
    </Stack>
  )
}

ServiceMultiSelectInput.defaultProps = defaultProps
